import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import PageLayout from '../components/PageLayout';
import ModernCard from '../components/ModernCard';
import ModernButton from '../components/ModernButton';
import SubscriptionManager from '../components/SubscriptionManager';
import CalendarActionModal from '../components/CalendarActionModal';
import DayEventsModal from '../components/DayEventsModal';
import TaskDrawer from '../components/workboard/TaskDrawer';
import OrderForm from '../components/OrderForm';
import OrderDetailModal from '../components/OrderDetailModal';
import { useTasks, Task } from '../hooks/useTasks';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../hooks/useOrders';
import { useInvoices } from '../hooks/useInvoices';
import { usePlanRestrictions } from '../hooks/usePlanRestrictions';
import toast from 'react-hot-toast';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock, 
  Users, 
  MapPin, 
  Bell,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  X,
  AlertTriangle,
  Save
} from 'lucide-react';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  type: 'meeting' | 'deadline' | 'reminder';
  category: 'task' | 'order' | 'invoice' | 'subscription' | 'calendar' | 'meeting';
  attendees?: string[];
  location?: string;
  priority: 'low' | 'medium' | 'high';
  description?: string;
  order_id?: string;
}

/**
 * Génère les dates récurrentes pour une subscription basée sur le billing_cycle
 * @param startDate Date de départ (next_renewal_date)
 * @param billingCycle Cycle de facturation (monthly, yearly, weekly, quarterly)
 * @param monthsAhead Nombre de mois à l'avance pour générer les occurrences (défaut: 12)
 * @returns Array de dates au format YYYY-MM-DD
 */
const generateRecurringDates = (startDate: string, billingCycle: string, monthsAhead: number = 12): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate + 'T00:00:00'); // Ajouter l'heure pour éviter les problèmes de timezone
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Mettre à minuit pour comparer seulement les dates
  
  // Calculer combien d'occurrences générer selon le cycle
  let occurrences = 0;
  switch (billingCycle) {
    case 'weekly':
      occurrences = Math.ceil((monthsAhead * 30) / 7); // ~4 semaines par mois
      break;
    case 'monthly':
      occurrences = monthsAhead;
      break;
    case 'quarterly':
      occurrences = Math.ceil(monthsAhead / 3); // 3 mois = 1 trimestre
      break;
    case 'yearly':
      occurrences = Math.ceil(monthsAhead / 12); // 12 mois = 1 an
      break;
    default:
      occurrences = monthsAhead; // Par défaut, mensuel
  }
  
  // Générer les dates récurrentes
  for (let i = 0; i < occurrences; i++) {
    const date = new Date(start);
    
    switch (billingCycle) {
      case 'weekly':
        date.setDate(date.getDate() + (i * 7));
        break;
      case 'monthly':
        // Ajouter i mois en préservant le jour du mois
        const dayOfMonth = date.getDate();
        date.setMonth(date.getMonth() + i);
        // Si le jour n'existe pas dans le nouveau mois (ex: 31 janvier -> 31 février), utiliser le dernier jour du mois
        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        if (dayOfMonth > lastDayOfMonth) {
          date.setDate(lastDayOfMonth);
        } else {
          date.setDate(dayOfMonth);
        }
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + (i * 3));
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + i);
        break;
      default:
        date.setMonth(date.getMonth() + i);
    }
    
    // Ne générer que les dates futures ou aujourd'hui
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    if (dateOnly >= now || i === 0) {
      const dateStr = date.toISOString().split('T')[0];
      dates.push(dateStr);
    }
  }
  
  return dates;
};

const PageCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { restrictions, checkAccess } = usePlanRestrictions();
  
  // Protection supplémentaire : rediriger si pas d'accès
  useEffect(() => {
    if (!restrictions?.isAdmin && !checkAccess('calendar')) {
      navigate('/dashboard', { replace: true });
    }
  }, [restrictions, checkAccess, navigate]);
  
  if (!restrictions?.isAdmin && !checkAccess('calendar')) {
    return null; // Ne rien afficher pendant la redirection
  }
  const { user } = useAuth();
  const { tasks, loading: tasksLoading, refetchTasks, createTask, updateTask, deleteTask } = useTasks();
  const { orders, loading: ordersLoading, refetchOrders } = useOrders();
  const { subscriptions, loading: subscriptionsLoading } = useSubscriptions();
  const { invoices, loading: invoicesLoading } = useInvoices();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // États pour les filtres par catégorie (checkboxes)
  const [filterCategories, setFilterCategories] = useState({
    task: true,
    order: true,
    invoice: true,
    subscription: true,
    calendar: true,
    meeting: true,
  });
  
  // États pour les modals
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isDayEventsModalOpen, setIsDayEventsModalOpen] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedTaskForDrawer, setSelectedTaskForDrawer] = useState<Task | null>(null);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<any>(null);
  const [selectedDateString, setSelectedDateString] = useState<string>('');
  
  // État du formulaire de tâche (identique au workboard)
  interface TaskFormData {
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    estimated_hours: number;
    due_date: string;
    order_id?: string;
  }
  
  const [taskForm, setTaskForm] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'medium',
    estimated_hours: 1,
    due_date: '',
    order_id: ''
  });

  // Mettre à jour la date du formulaire quand on ouvre le modal avec une date sélectionnée
  useEffect(() => {
    if (showTaskModal && !editingTask && selectedDateString) {
      // Mettre à jour la date quand le modal s'ouvre avec une nouvelle tâche
      setTaskForm(prev => ({
        ...prev,
        due_date: selectedDateString
      }));
    }
  }, [showTaskModal, editingTask, selectedDateString]);

  // Helper function to apply all filters
  const applyFilters = (eventsToFilter: Event[]) => {
    // Apply category filters
    let filtered = eventsToFilter.filter(event => {
      return filterCategories[event.category as keyof typeof filterCategories];
    });
    
    // Apply type filters
    if (filterType) {
      filtered = filtered.filter(event => event.type === filterType);
    }
    if (filterPriority) {
      filtered = filtered.filter(event => event.priority === filterPriority);
    }
    
    return filtered;
  };

  // Fonctions de gestion des modals
  const handleDateClick = (dateString: string) => {
    // Normaliser le format de date (YYYY-MM-DD) - éviter les problèmes de timezone
    let normalizedDate = dateString;
    
    if (normalizedDate.includes('T')) {
      normalizedDate = normalizedDate.split('T')[0];
    }
    
    // Si ce n'est pas déjà au format YYYY-MM-DD, extraire manuellement
    if (!normalizedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const date = new Date(dateString);
      // Utiliser les méthodes locales pour éviter les problèmes de timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      normalizedDate = `${year}-${month}-${day}`;
    }
    
    setSelectedDateString(normalizedDate);
    // Vérifier s'il y a des événements pour cette date (avec filtres appliqués)
    const dayEvents = events.filter(event => event.date === normalizedDate);
    const filteredDayEvents = applyFilters(dayEvents);
    if (filteredDayEvents.length > 0) {
      setIsDayEventsModalOpen(true);
    } else {
      setIsActionModalOpen(true);
    }
  };

  const handleTaskSelect = () => {
    // Initialiser le formulaire avec la date sélectionnée du calendrier
    let defaultDate = selectedDateString;
    if (!defaultDate) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      defaultDate = `${year}-${month}-${day}`;
    }
    setTaskForm({
      title: '',
      description: '',
      priority: 'medium',
      estimated_hours: 1,
      due_date: defaultDate,
      order_id: ''
    });
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleOrderSelect = () => {
    setEditingOrder(null);
    setIsOrderFormOpen(true);
  };

  const handleAddEvent = () => {
    // S'assurer que selectedDateString est défini
    if (!selectedDateString) {
      const today = new Date().toISOString().split('T')[0];
      setSelectedDateString(today);
    }
    setIsActionModalOpen(true);
  };

  const handleEventClick = (event: any) => {
    // Si c'est une tâche, ouvrir TaskDrawer
    if (event.type === 'deadline' && event.id.startsWith('task-')) {
      const taskId = event.id.replace('task-', '');
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTaskForDrawer(task);
        setIsTaskDrawerOpen(true);
      }
    }
    // Si c'est une commande, ouvrir OrderDetailModal
    else if (event.category === 'order' && event.id.startsWith('order-')) {
      const orderId = event.id.replace('order-', '');
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setSelectedOrderForDetail(order);
      }
    }
  };

  // Gestion du formulaire de tâche (identique au workboard)
  const handleTaskFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    try {
      // Utiliser la date sélectionnée du calendrier si due_date est vide
      let finalDueDate = taskForm.due_date;
      if (!finalDueDate && selectedDateString) {
        finalDueDate = selectedDateString;
      }
      
      // Normaliser le format de date (YYYY-MM-DD) - éviter les problèmes de timezone
      if (finalDueDate) {
        // Si la date contient un 'T', on la normalise
        if (finalDueDate.includes('T')) {
          finalDueDate = finalDueDate.split('T')[0];
        }
        // Si la date est un objet Date ou timestamp, on la convertit correctement
        else if (finalDueDate.length > 10) {
          const date = new Date(finalDueDate);
          // Utiliser getFullYear, getMonth, getDate pour éviter les problèmes de timezone
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          finalDueDate = `${year}-${month}-${day}`;
        }
      }
      
      // Valider que le status est autorisé par la contrainte de la base de données
      const validStatuses = ['todo', 'in_progress', 'completed'];
      
      const taskData = {
        title: taskForm.title.trim(),
        description: taskForm.description.trim() || null,
        priority: taskForm.priority,
        estimated_hours: taskForm.estimated_hours,
        due_date: finalDueDate,
        order_id: taskForm.order_id || null,
        status: 'todo' as 'todo' | 'in_progress' | 'completed'
      };

      if (editingTask) {
        const success = await updateTask(editingTask.id, taskData);
        if (!success) {
          toast.error('Failed to update task');
          return;
        }
        toast.success('Task updated successfully!');
      } else {
        const success = await createTask(taskData);
        if (!success) {
          toast.error('Failed to create task');
          return;
        }
        toast.success('Task created successfully!');
      }

      // Reset form avec la date sélectionnée
      const defaultDate = selectedDateString || new Date().toISOString().split('T')[0];
      setTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        estimated_hours: 1,
        due_date: defaultDate,
        order_id: ''
      });
      setShowTaskModal(false);
      setEditingTask(null);
      
      // Recharger les données
      await refetchTasks();
      await loadCalendarEvents();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    // Pour l'édition, utiliser la due_date de la tâche ou la date sélectionnée
    const taskDueDate = task.due_date || selectedDateString || new Date().toISOString().split('T')[0];
    setTaskForm({
      title: task.title,
      description: task.description || '',
      priority: task.priority as 'low' | 'medium' | 'high',
      estimated_hours: task.estimated_hours || 1,
      due_date: taskDueDate,
      order_id: task.order_id || ''
    });
    setShowTaskModal(true);
    setIsTaskDrawerOpen(false);
    setSelectedTaskForDrawer(null);
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      toast.success('Task deleted successfully!');
      setIsTaskDrawerOpen(false);
      setSelectedTaskForDrawer(null);
      await refetchTasks();
      await loadCalendarEvents();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      // Valider que le status est autorisé par la contrainte de la base de données
      const validStatuses = ['todo', 'in_progress', 'completed'];
      const validStatus = validStatuses.includes(newStatus) 
        ? newStatus as 'todo' | 'in_progress' | 'completed'
        : 'todo';
      
      if (!validStatuses.includes(newStatus)) {
        console.warn(`Invalid status "${newStatus}", defaulting to "todo"`);
      }
      
      await updateTask(taskId, { status: validStatus });
      toast.success('Task status updated!');
      await refetchTasks();
      await loadCalendarEvents();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const resetTaskForm = () => {
    const defaultDate = selectedDateString || new Date().toISOString().split('T')[0];
    setTaskForm({
      title: '',
      description: '',
      priority: 'medium',
      estimated_hours: 1,
      due_date: defaultDate,
      order_id: ''
    });
    setEditingTask(null);
  };

  // Fermer le dropdown de filtre quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-button]')) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  // Fonction pour charger les événements du calendrier depuis la base de données
  const loadCalendarEvents = useCallback(async () => {
      if (!isSupabaseConfigured || !supabase || !user) {
        console.log('Supabase not configured or no user');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📅 Loading calendar events...');

        // Récupérer les événements du calendrier
        const { data: calendarData, error: calendarError } = await supabase
          .from('calendar_events')
          .select('*')
          .eq('user_id', user.id)
          .order('start_time', { ascending: true });

        if (calendarError) {
          console.error('Error loading calendar events:', calendarError);
        }

        setCalendarEvents(calendarData || []);

        // Convertir les factures avec due_date en événements
        const invoiceEvents: Event[] = invoices
          .filter(invoice => invoice.due_date)
          .map(invoice => {
            console.log('🧾 Invoice with due_date:', {
              id: invoice.id,
              number: invoice.number,
              due_date: invoice.due_date,
              total: invoice.total,
              status: invoice.status
            });
            return {
              id: `invoice-${invoice.id}`,
              title: `🧾 ${invoice.number} - ${invoice.clients.name}`,
              date: invoice.due_date,
              time: '17:00',
              type: 'deadline' as const,
              category: 'invoice' as const,
              priority: invoice.status === 'overdue' ? 'high' as const : 'medium' as const,
              description: `Facture de ${invoice.total.toFixed(2)} ${invoice.currency} - ${invoice.status}`
            };
          });

        // Convertir les tâches avec due_date en événements
        const taskEvents: Event[] = tasks
          .filter(task => task.due_date)
          .map(task => {
            console.log('📋 Task with due_date:', {
              id: task.id,
              title: task.title,
              due_date: task.due_date,
              priority: task.priority
            });
            return {
              id: `task-${task.id}`,
              title: `📋 ${task.title}`,
              date: task.due_date!,
              time: '09:00',
              type: 'deadline' as const,
              category: 'task' as const,
              priority: task.priority as 'low' | 'medium' | 'high',
              description: task.description
            };
          });

        // Convertir TOUTES les tâches avec due_date en événements (comme les commandes)
        const allTaskEvents: Event[] = tasks
          .filter(task => task.due_date) // Filtrer seulement les tâches avec due_date
          .map(task => {
            // Normaliser le format de date pour correspondre aux commandes
            let eventDate: string;
            if (task.due_date) {
              // Si c'est déjà une date au format YYYY-MM-DD, l'utiliser directement
              if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                eventDate = task.due_date;
              } else {
                // Sinon, convertir en format YYYY-MM-DD en utilisant les méthodes locales
                const date = new Date(task.due_date);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                eventDate = `${year}-${month}-${day}`;
              }
            } else {
              eventDate = new Date().toISOString().split('T')[0];
            }
            
            console.log('📋 Task event created:', {
              id: task.id,
              title: task.title,
              due_date: task.due_date,
              eventDate: eventDate,
              priority: task.priority
            });
            
          return {
            id: `task-${task.id}`,
            title: `📋 ${task.title}`,
            date: eventDate,
            time: '09:00',
            type: 'deadline' as const,
            category: 'task' as const,
            priority: task.priority as 'low' | 'medium' | 'high',
              description: task.description || '',
            order_id: task.order_id // Ajouter l'order_id pour la navigation
          };
        });

        // Convertir les commandes avec due_date en événements
        const orderEvents: Event[] = orders
          .filter(order => order.due_date)
          .map(order => {
            console.log('📦 Order with due_date:', {
              id: order.id,
              title: order.title,
              due_date: order.due_date,
              budget: order.budget
            });
            return {
              id: `order-${order.id}`,
              title: `📦 ${order.title}`,
              date: order.due_date!,
              time: '17:00', // Heure par défaut pour les deadlines
              type: 'deadline' as const,
              category: 'order' as const,
              priority: 'high' as const, // Les deadlines de commandes sont importantes
              description: `Commande pour ${order.client?.name || 'Client'}`
            };
          });

        // Convertir les événements du calendrier en format Event
        const calendarEventObjects: Event[] = (calendarData || []).map(event => ({
          id: `event-${event.id}`,
          title: event.title,
          date: event.start_time.split('T')[0],
          time: event.start_time.split('T')[1]?.substring(0, 5) || '09:00',
          type: event.type || 'meeting' as const,
          category: (event.type || 'meeting') === 'meeting' ? 'meeting' as const : 'calendar' as const,
          priority: event.priority || 'medium' as const,
          attendees: event.attendees || [],
          location: event.location
        }));

        // Convertir les abonnements en événements de renouvellement récurrents
        const subscriptionEvents: Event[] = [];
        subscriptions
          .filter(sub => sub.is_active && sub.next_renewal_date)
          .forEach(sub => {
            // Générer les dates récurrentes (12 mois à l'avance)
            const recurringDates = generateRecurringDates(
              sub.next_renewal_date!,
              sub.billing_cycle,
              12
            );
            
            // Créer un événement pour chaque date récurrente
            recurringDates.forEach((date, index) => {
              subscriptionEvents.push({
                id: `subscription-${sub.id}-${index}`,
                title: `💳 ${sub.name} - $${sub.amount}`,
                date: date,
                time: '09:00',
                type: 'reminder' as const,
                category: 'subscription' as const,
                priority: 'medium' as const,
                description: `Renouvellement ${sub.billing_cycle} - ${sub.provider}`
              });
            });
            
            console.log('💳 Subscription renewal events generated:', {
              id: sub.id,
              name: sub.name,
              renewal_date: sub.next_renewal_date,
              billing_cycle: sub.billing_cycle,
              occurrences: recurringDates.length,
              dates: recurringDates.slice(0, 3).join(', ') + (recurringDates.length > 3 ? '...' : '')
            });
          });

        // Combiner tous les événements
        const allEvents = [...allTaskEvents, ...orderEvents, ...calendarEventObjects, ...subscriptionEvents, ...invoiceEvents];
        setEvents(allEvents);
        
        console.log('✅ Calendar events loaded:', {
          tasks: allTaskEvents.length,
          taskEvents: allTaskEvents.map(e => ({ id: e.id, title: e.title, date: e.date })),
          orders: orderEvents.length,
          orderEvents: orderEvents.map(e => ({ id: e.id, title: e.title, date: e.date })),
          calendar: calendarEventObjects.length,
          subscriptions: subscriptionEvents.length,
          invoices: invoiceEvents.length,
          total: allEvents.length
        });
        
        // Debug: vérifier les tâches avec due_date
        const tasksWithDueDate = tasks.filter(t => t.due_date);
        console.log('📋 Tasks with due_date:', tasksWithDueDate.length, tasksWithDueDate.map(t => ({
          id: t.id,
          title: t.title,
          due_date: t.due_date
        })));
        
        // Debug: afficher les tâches sans due_date
        const tasksWithoutDueDate = tasks.filter(task => !task.due_date);
        if (tasksWithoutDueDate.length > 0) {
          console.log('⚠️ Tasks without due_date:', tasksWithoutDueDate.map(t => ({
            id: t.id,
            title: t.title,
            due_date: t.due_date
          })));
        }
        
        // Debug: afficher toutes les tâches
        console.log('📋 All tasks:', tasks.map(t => ({
          id: t.id,
          title: t.title,
          due_date: t.due_date,
          status: t.status
        })));
      } catch (error) {
        console.error('Error loading calendar data:', error);
      } finally {
        setLoading(false);
      }
  }, [user, tasks, orders, subscriptions, invoices]);

  // Charger les événements du calendrier quand les données sont prêtes ou quand on déclenche un refresh
  useEffect(() => {
    // Charger seulement si les données sont prêtes
    if (!tasksLoading && !ordersLoading && !subscriptionsLoading && !invoicesLoading) {
      loadCalendarEvents();
    }
  }, [tasksLoading, ordersLoading, subscriptionsLoading, invoicesLoading, refreshTrigger, loadCalendarEvents]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(year, month, -i);
      days.push({ date: day, isCurrentMonth: false });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    let filteredEvents = events.filter(event => {
      // Normaliser les dates pour la comparaison
      let eventDateStr = event.date;
      if (!eventDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Si la date n'est pas au bon format, la convertir
        try {
          const eventDate = new Date(eventDateStr);
          eventDateStr = eventDate.toISOString().split('T')[0];
        } catch (e) {
          console.warn('Invalid date format for event:', event.id, eventDateStr);
          return false;
        }
      }
      return eventDateStr === dateStr;
    });
    
    // Apply all filters
    filteredEvents = applyFilters(filteredEvents);
    
    return filteredEvents;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-gradient-to-r from-[#f43f5e] to-[#e11d48]';
      case 'medium': return 'bg-gradient-to-r from-[#f59e0b] to-[#d97706]';
      case 'low': return 'bg-gradient-to-r from-[#22c55e] to-[#16a34a]';
      default: return 'bg-gradient-to-r from-[#64748b] to-[#475569]';
    }
  };

  const getPriorityBorderColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-[#f43f5e]';
      case 'medium': return 'border-[#f59e0b]';
      case 'low': return 'border-[#22c55e]';
      default: return 'border-[#64748b]';
    }
  };

  const getPriorityDotColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-[#f43f5e]';
      case 'medium': return 'bg-[#f59e0b]';
      case 'low': return 'bg-[#22c55e]';
      default: return 'bg-[#64748b]';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'meeting': return <Users size={12} className="text-[rgb(var(--fg))]" />;
      case 'deadline': return <Clock size={12} className="text-[rgb(var(--fg))]" />;
      case 'reminder': return <Bell size={12} className="text-[rgb(var(--fg))]" />;
      default: return <CalendarIcon size={12} className="text-[rgb(var(--fg))]" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'bg-gradient-to-r from-[#3b82f6] to-[#2563eb]';
      case 'deadline': return 'bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed]';
      case 'reminder': return 'bg-gradient-to-r from-[#06b6d4] to-[#0891b2]';
      default: return 'bg-gradient-to-r from-[#64748b] to-[#475569]';
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    }).replace(/^\w/, c => c.toUpperCase());
  };

  const days = getDaysInMonth(currentDate);
  const currentMonthIndex = currentDate.getMonth();
  const currentYearValue = currentDate.getFullYear();

  const getEventDateTime = (event: Event) => {
    const baseDate = event.date.includes('T') ? event.date.split('T')[0] : event.date;
    const timeValue = event.time
      ? (event.time.length === 5 ? `${event.time}:00` : event.time)
      : '09:00:00';
    return new Date(`${baseDate}T${timeValue}`);
  };

  const monthEvents = events
    .filter(event => {
      const dateKey = event.date.includes('T') ? event.date.split('T')[0] : event.date;
      const eventDate = new Date(`${dateKey}T00:00:00`);
      return eventDate.getMonth() === currentMonthIndex && eventDate.getFullYear() === currentYearValue;
    })
    .sort((a, b) => getEventDateTime(a).getTime() - getEventDateTime(b).getTime());

  const mobileEventsByDay = monthEvents.reduce<Record<string, Event[]>>((acc, event) => {
    const key = event.date.includes('T') ? event.date.split('T')[0] : event.date;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(event);
    return acc;
  }, {});

  const mobileEventEntries = Object.entries(mobileEventsByDay).sort(
    (a, b) =>
      new Date(`${a[0]}T00:00:00`).getTime() - new Date(`${b[0]}T00:00:00`).getTime()
  );

  // Afficher un indicateur de chargement
  if (loading || tasksLoading || ordersLoading || subscriptionsLoading || invoicesLoading) {
    return (
      <div className="p-6 space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-[rgb(var(--muted))]">Loading calendar events...</p>
            </div>
          </div>
        </div>
    );
  }

  return (
    <PageWrapper>
      <PageLayout
        title="Calendar"
        actions={
          <div className="flex gap-3">
            <div className="relative">
            <ModernButton 
              variant="outline" 
              size="sm"
              data-filter-button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="text-sm h-10 px-3 sm:px-4 min-h-[44px]"
            >
              <Filter size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Filters</span>
            </ModernButton>
              
              {/* Dropdown Menu */}
              {showFilterDropdown && (
                <div className="filter-dropdown absolute top-full right-0 mt-2 w-64 bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-[rgb(var(--border))] flex items-center justify-between sticky top-0 bg-[rgb(var(--card))]">
                    <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Show Events</h3>
                    <button
                      onClick={() => {
                        setFilterCategories({
                          task: true,
                          order: true,
                          invoice: true,
                          subscription: true,
                          calendar: true,
                          meeting: true,
                        });
                        setFilterType('');
                        setFilterPriority('');
                      }}
                      className="text-xs text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-colors"
                    >
                      Reset
                    </button>
                  </div>

                  {/* Category Filters */}
                  <div className="py-2">
                    {[
                      { key: 'task', label: 'Tasks', icon: '📋' },
                      { key: 'order', label: 'Orders', icon: '📦' },
                      { key: 'invoice', label: 'Invoices', icon: '🧾' },
                      { key: 'subscription', label: 'Subscriptions', icon: '💳' },
                      { key: 'calendar', label: 'Calendar Events', icon: '📅' },
                      { key: 'meeting', label: 'Meetings', icon: '👥' },
                    ].map(({ key, label, icon }) => (
                      <label
                        key={key}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-[rgb(var(--card))] cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={filterCategories[key as keyof typeof filterCategories]}
                          onChange={(e) => {
                            setFilterCategories(prev => ({
                              ...prev,
                              [key]: e.target.checked
                            }));
                          }}
                          className="w-4 h-4 rounded border-gray-500 bg-[rgb(var(--card))] text-[#9c68f2] focus:ring-[#9c68f2] focus:ring-offset-[#1e2938] focus:ring-offset-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="flex-1 text-sm text-[rgb(var(--fg))]">{icon} {label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-[rgb(var(--border))]"></div>

                  {/* Event Type Filters */}
                  <div className="py-2">
                    <div className="px-4 py-2">
                      <span className="text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wide">Event Type</span>
                    </div>
                    {[
                      { value: '', label: 'All Types' },
                      { value: 'meeting', label: 'Meetings', icon: '👥' },
                      { value: 'deadline', label: 'Deadlines', icon: '⚡' },
                      { value: 'reminder', label: 'Reminders', icon: '🔔' },
                    ].map(({ value, label, icon }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setFilterType(value);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                          filterType === value 
                            ? 'text-[rgb(var(--fg))] bg-[#9c68f2]/20' 
                            : 'text-[rgb(var(--muted))] hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--fg))]'
                        }`}
                      >
                        {icon && <span>{icon}</span>}
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Priority Filters */}
                  <div className="py-2 pb-2">
                    <div className="px-4 py-2">
                      <span className="text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wide">Priority</span>
                    </div>
                    {[
                      { value: '', label: 'All Priorities' },
                      { value: 'high', label: 'High', icon: '🔴' },
                      { value: 'medium', label: 'Medium', icon: '🟡' },
                      { value: 'low', label: 'Low', icon: '🟢' },
                    ].map(({ value, label, icon }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setFilterPriority(value);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                          filterPriority === value 
                            ? 'text-[rgb(var(--fg))] bg-[#9c68f2]/20' 
                            : 'text-[rgb(var(--muted))] hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--fg))]'
                        }`}
                      >
                        {icon && <span>{icon}</span>}
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ModernButton 
              size="sm"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setSelectedDateString(today);
                setIsActionModalOpen(true);
              }}
              className="text-sm h-10 px-3 sm:px-4 min-h-[44px]"
            >
              <Plus size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">New Event</span>
              <span className="sm:hidden">New</span>
            </ModernButton>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Calendrier principal */}
          <div className="lg:col-span-3">
            <ModernCard title="Calendar" icon={<CalendarIcon size={20} className="text-[rgb(var(--fg))]" />}>
              {/* Navigation du calendrier - Compact on mobile */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-2 rounded-lg hover:bg-[rgb(var(--card))] transition-colors"
                >
                  <ChevronLeft size={20} className="text-[rgb(var(--fg))]" />
                </button>
                
                <h2 className="text-xl font-semibold text-[rgb(var(--fg))]">
                  {formatMonthYear(currentDate)}
                </h2>
                
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-2 rounded-lg hover:bg-[rgb(var(--card))] transition-colors"
                >
                  <ChevronRight size={20} className="text-[rgb(var(--fg))]" />
                </button>
              </div>

              {/* Grille du calendrier - Hide on mobile, show agenda instead */}
              <div className="hidden sm:grid grid-cols-7 gap-1 mb-4">
                {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                  <div key={day} className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-[rgb(var(--muted))]">
                    {day}
                  </div>
                ))}
              </div>

              {/* Mobile: condensed agenda view */}
              <div className="sm:hidden space-y-4 mb-4">
                {mobileEventEntries.length === 0 ? (
                  <div className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] py-10 px-6 text-center">
                    <p className="text-sm font-semibold text-[rgb(var(--fg))]">Aucun évènement ce mois-ci</p>
                    <p className="text-xs text-[rgb(var(--muted))] mt-1">
                      Ajoutez un évènement avec le bouton&nbsp;
                      <span className="font-medium">New</span>.
                    </p>
                  </div>
                ) : (
                  mobileEventEntries.map(([dateKey, dayEvents]) => {
                    const dateObj = new Date(`${dateKey}T00:00:00`);
                    const formattedDay = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
                    const weekday = dateObj.toLocaleDateString('fr-FR', { weekday: 'long' });

                    return (
                      <div
                        key={dateKey}
                        className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] shadow-sm overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-[rgb(var(--border))] flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-[rgb(var(--muted))]">
                              {weekday}
                            </p>
                            <p className="text-base font-semibold text-[rgb(var(--fg))]">
                              {formattedDay}
                            </p>
                          </div>
                          <span className="text-xs font-medium text-[rgb(var(--muted))]">
                            {dayEvents.length} {dayEvents.length > 1 ? 'évènements' : 'évènement'}
                          </span>
                        </div>

                        <div className="divide-y divide-[rgb(var(--border))]">
                          {dayEvents.map((event) => {
                            const timeLabel = event.time || '09:00';

                            return (
                              <button
                                key={event.id}
                                type="button"
                                onClick={() => handleEventClick(event)}
                                className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[rgb(var(--card))]/60 transition-colors"
                              >
                                <div className={`mt-1 h-2 w-2 rounded-full ${getPriorityDotColor(event.priority)}`} />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-semibold text-[rgb(var(--fg))]">
                                      {event.title}
                                    </span>
                                    <span className="text-xs text-[rgb(var(--muted))] whitespace-nowrap">
                                      {timeLabel}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex items-center gap-2 text-xs text-[rgb(var(--muted))]">
                                    <span className="inline-flex items-center gap-1">
                                      {getTypeIcon(event.type)}
                                      <span className="capitalize">{event.type}</span>
                                    </span>
                                    {event.location && (
                                      <span className="inline-flex items-center gap-1">
                                        <MapPin size={10} />
                                        {event.location}
                                      </span>
                                    )}
                                  </div>
                                  {event.description && (
                                    <p className="mt-2 text-xs text-[rgb(var(--muted))] leading-relaxed line-clamp-2">
                                      {event.description}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Desktop: Calendar grid */}
              <div className="hidden sm:grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  const dayEvents = getEventsForDate(day.date);
                  const isToday = day.date.toDateString() === new Date().toDateString();
                  const isSelected = selectedDate?.toDateString() === day.date.toDateString();
                  
                  return (
                    <div
                      key={index}
                      className={`
                        min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 border border-[rgb(var(--border))] rounded-lg cursor-pointer transition-colors
                        ${day.isCurrentMonth ? 'bg-[rgb(var(--card))]' : 'bg-[#111726] opacity-50'}
                        ${isToday ? 'border-[#9c68f2] bg-[#9c68f2]/10' : ''}
                        ${isSelected ? 'bg-[rgb(var(--card))]' : 'hover:bg-[rgb(var(--card))]/50'}
                      `}
                      onClick={() => {
                        setSelectedDate(day.date);
                        // Utiliser les méthodes locales pour éviter les problèmes de timezone
                        const year = day.date.getFullYear();
                        const month = String(day.date.getMonth() + 1).padStart(2, '0');
                        const dayNum = String(day.date.getDate()).padStart(2, '0');
                        handleDateClick(`${year}-${month}-${dayNum}`);
                      }}
                    >
                      <div className={`text-sm font-medium ${day.isCurrentMonth ? 'text-[rgb(var(--fg))]' : 'text-gray-500'}`}>
                        {day.date.getDate()}
                      </div>
                      
                      {/* Événements du jour */}
                      <div className="mt-1 space-y-1">
                        {dayEvents.slice(0, 2).map(event => (
                          <div
                            key={event.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(event);
                            }}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-[rgb(var(--fg))] shadow-sm border ${getTypeColor(event.type)} ${getPriorityBorderColor(event.priority)} hover:shadow-md transition-all duration-200 cursor-pointer group`}
                          >
                            <div className="flex-shrink-0">
                              {getTypeIcon(event.type)}
                            </div>
                            <span className="truncate font-medium group-hover:font-semibold transition-all duration-200">{event.title}</span>
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-[#94a3b8] bg-[rgb(var(--card))] px-2 py-1 rounded-lg border border-[rgb(var(--border))] hover:bg-[rgb(var(--card))] transition-colors cursor-pointer">
                            +{dayEvents.length - 2} more events
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ModernCard>
          </div>

          {/* Sidebar - Stack on mobile */}
          <div className="space-y-4 sm:space-y-6">
            {/* Événements d'aujourd'hui */}
            <ModernCard title="Today" icon={<Clock size={20} className="text-[rgb(var(--fg))]" />}>
              <div className="space-y-3">
                {getEventsForDate(new Date()).map(event => (
                  <div 
                    key={event.id} 
                    className="p-4 bg-[rgb(var(--card))] rounded-xl border border-[rgb(var(--border))] shadow-lg hover:shadow-xl hover:border-[rgb(var(--border))] transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {getTypeIcon(event.type)}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-[rgb(var(--fg))] group-hover:text-[rgb(var(--fg))] transition-colors">{event.title}</span>
                          <div className="text-xs text-[#94a3b8] mt-1">{event.time}</div>
                        </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${getPriorityColor(event.priority)} shadow-sm`} />
                    </div>
                    {event.attendees && event.attendees.length > 0 && (
                      <div className="text-xs text-[#94a3b8] bg-[rgb(var(--card))] px-2 py-1 rounded-lg inline-block">
                        👥 {event.attendees.length} participant{event.attendees.length > 1 ? 's' : ''}
                      </div>
                    )}
                    {event.location && (
                      <div className="text-xs text-[#94a3b8] mt-2 flex items-center gap-1">
                        <MapPin size={10} />
                        {event.location}
                      </div>
                    )}
                  </div>
                ))}
                {getEventsForDate(new Date()).length === 0 && (
                  <div className="text-center text-[#94a3b8] py-8">
                    <div className="w-16 h-16 bg-[rgb(var(--card))] rounded-full flex items-center justify-center mx-auto mb-3">
                      <CalendarIcon size={24} className="text-[#64748b]" />
                    </div>
                    <p className="text-sm font-medium">No events today</p>
                    <p className="text-xs text-[#64748b] mt-1">Enjoy your free time!</p>
                  </div>
                )}
              </div>
            </ModernCard>

            {/* Statistiques réelles */}
            <ModernCard title="Statistics" icon={<Users size={20} className="text-[rgb(var(--fg))]" />}>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb]"></div>
                    <span className="text-sm text-[#94a3b8]">Total events</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">{events.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed]"></div>
                    <span className="text-sm text-[#94a3b8]">Tasks with deadline</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {tasks.filter(task => task.due_date).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#f59e0b] to-[#d97706]"></div>
                    <span className="text-sm text-[#94a3b8]">Orders with due_date</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {orders.filter(order => order.due_date).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#ec4899] to-[#db2777]"></div>
                    <span className="text-sm text-[#94a3b8]">Invoice due dates</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {invoices.filter(invoice => invoice.due_date).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#06b6d4] to-[#0891b2]"></div>
                    <span className="text-sm text-[#94a3b8]">Events this month</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {events.filter(event => {
                      const eventDate = new Date(event.date);
                      const currentMonth = currentDate.getMonth();
                      const currentYear = currentDate.getFullYear();
                      return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
                    }).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a]"></div>
                    <span className="text-sm text-[#94a3b8]">Active subscriptions</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {subscriptions.filter(sub => sub.is_active).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--border))] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#f43f5e] to-[#e11d48]"></div>
                    <span className="text-sm text-[#94a3b8]">Renewals this month</span>
                  </div>
                  <span className="text-lg font-bold text-[rgb(var(--fg))]">
                    {subscriptions.filter(sub => {
                      if (!sub.is_active || !sub.next_renewal_date) return false;
                      const renewalDate = new Date(sub.next_renewal_date);
                      const currentMonth = currentDate.getMonth();
                      const currentYear = currentDate.getFullYear();
                      return renewalDate.getMonth() === currentMonth && renewalDate.getFullYear() === currentYear;
                    }).length}
                  </span>
                </div>
              </div>
            </ModernCard>
          </div>
        </div>

        {/* Subscription Management Section */}
        <div className="mt-6">
          <SubscriptionManager />
        </div>

        {/* Modals */}
        <CalendarActionModal
          isOpen={isActionModalOpen}
          onClose={() => {
            setIsActionModalOpen(false);
            if (!selectedDateString) {
              const today = new Date().toISOString().split('T')[0];
              setSelectedDateString(today);
            }
          }}
          selectedDate={selectedDateString || new Date().toISOString().split('T')[0]}
          onSelectTask={handleTaskSelect}
          onSelectOrder={handleOrderSelect}
        />

        <DayEventsModal
          isOpen={isDayEventsModalOpen}
          onClose={() => setIsDayEventsModalOpen(false)}
          selectedDate={selectedDateString}
          events={applyFilters(events.filter(event => event.date === selectedDateString))}
          onAddEvent={handleAddEvent}
          onEventClick={handleEventClick}
        />

        {/* Task Form Modal (identique au workboard) */}
        {showTaskModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[rgb(var(--card))] rounded-xl border border-[rgb(var(--border))] w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-[rgb(var(--border))]">
                <h3 className="text-xl font-semibold text-[rgb(var(--fg))]">
                  {editingTask ? 'Edit Task' : 'New Task'}
                </h3>
                <button
                  onClick={() => {
                    setShowTaskModal(false);
                    resetTaskForm();
                  }}
                  className="text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleTaskFormSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] placeholder-gray-400 focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                    placeholder="Enter task title..."
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Description
                  </label>
                  <textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] placeholder-gray-400 focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                    placeholder="Enter task description..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Order (Optional)
                  </label>
                  <select
                    value={taskForm.order_id || ''}
                    onChange={(e) => setTaskForm({...taskForm, order_id: e.target.value || undefined})}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                  >
                    <option value="">No Order</option>
                    {orders.map(order => (
                      <option key={order.id} value={order.id}>
                        {order.title} - {order.budget ? `$${order.budget}` : 'No Budget'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                      Priority
                    </label>
                    <select
                      value={taskForm.priority}
                      onChange={(e) => setTaskForm({...taskForm, priority: e.target.value as any})}
                      className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={taskForm.estimated_hours}
                      onChange={(e) => setTaskForm({...taskForm, estimated_hours: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] placeholder-gray-400 focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2]"
                  />
                </div>
                
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <ModernButton
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowTaskModal(false);
                      resetTaskForm();
                    }}
                  >
                    Cancel
                  </ModernButton>
                  <ModernButton type="submit">
                    <Save className="mr-2" size={16} />
                    {editingTask ? 'Update Task' : 'Create Task'}
                  </ModernButton>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Task Drawer */}
        <TaskDrawer
          task={selectedTaskForDrawer}
          isOpen={isTaskDrawerOpen}
          onClose={() => {
            setIsTaskDrawerOpen(false);
            setSelectedTaskForDrawer(null);
          }}
          onUpdate={async (taskId, updates) => {
            const success = await updateTask(taskId, updates);
            if (success) {
              await refetchTasks();
              await loadCalendarEvents();
              setRefreshTrigger(prev => prev + 1);
              // Update selected task if it's the same one
              if (selectedTaskForDrawer && selectedTaskForDrawer.id === taskId) {
                const updatedTask = tasks.find(t => t.id === taskId);
                if (updatedTask) {
                  setSelectedTaskForDrawer(updatedTask);
                }
              }
            }
          }}
          onDelete={async (taskId) => {
            const success = await deleteTask(taskId);
            if (success) {
              await refetchTasks();
              await loadCalendarEvents();
              setRefreshTrigger(prev => prev + 1);
              setIsTaskDrawerOpen(false);
              setSelectedTaskForDrawer(null);
            }
          }}
          orders={orders.map(o => ({ id: o.id, title: o.title }))}
        />
      </PageLayout>

      {/* Modales - en dehors du PageLayout */}
      <OrderForm
        isOpen={isOrderFormOpen}
        onClose={() => {
          setIsOrderFormOpen(false);
          setEditingOrder(null);
        }}
        onSuccess={async () => {
          await refetchOrders();
          await loadCalendarEvents();
          setRefreshTrigger(prev => prev + 1);
        }}
        selectedDate={selectedDateString || undefined}
        order={editingOrder ? {
          id: editingOrder.id,
          title: editingOrder.title,
          description: editingOrder.description,
          status: editingOrder.status,
          platform: editingOrder.platform,
          client_id: editingOrder.client_id,
          budget: editingOrder.budget,
          currency: editingOrder.currency || 'USD',
          start_date: editingOrder.start_date,
          due_date: editingOrder.due_date,
          completed_date: editingOrder.completed_date
        } : null}
      />

      <OrderDetailModal
          isOpen={!!selectedOrderForDetail}
          onClose={() => setSelectedOrderForDetail(null)}
          order={selectedOrderForDetail ? {
            id: selectedOrderForDetail.id,
            title: selectedOrderForDetail.title,
            description: selectedOrderForDetail.description,
            budget: selectedOrderForDetail.budget,
            due_date: selectedOrderForDetail.due_date,
            created_at: selectedOrderForDetail.created_at,
            status: selectedOrderForDetail.status,
            start_date: selectedOrderForDetail.start_date,
            completion_date: selectedOrderForDetail.completed_date,
            clients: selectedOrderForDetail.client ? {
              name: selectedOrderForDetail.client.name,
              platform: selectedOrderForDetail.platform
            } : undefined,
            platform: selectedOrderForDetail.platform
          } : null}
          onOrderUpdated={async (updatedOrder) => {
            await refetchOrders();
            await loadCalendarEvents();
            setRefreshTrigger(prev => prev + 1);
          }}
      />

    </PageWrapper>
  );
};

export default PageCalendar;
